import { Button } from '@components/Button.tsx';
import DirectusTable, {
  type DirectusTableHandle,
} from '@components/directusTable/DirectusTable.tsx';
import { type Query } from '@directus/sdk';
import {
  type CustomDirectusTypes,
  type Organization,
} from '@funnellab/shared/src/types/directus.ts';
import { directusClient } from '@src/api/DirectusClient.ts';
import { organizationTableQuery } from '@src/api/queries/organization.ts';
import { DefaultView } from '@src/components/base/DefaultView';
import { NO_PAGINATION_PAGE_SIZE } from '@src/constants.ts';
import { IconType } from '@src/IconType.ts';
import { Paths, getNewJobWizardUrl } from '@src/paths.ts';
import { ButtonType } from '@src/types.ts';
import { type OrganizationsListViewLoaderData } from '@src/views/organizations/OrganizationsListView.functions.ts';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import { AddOrganizationDialog } from './AddOrganizationDialog';
import { Body5 } from '@src/components/typography/Body5';
import { Body4 } from '@src/components/typography/Body4';
import { useUserContext } from '@src/contexts/useUserContext';
import { RowActions } from '@src/components/organizationOverview/RowActions';

type OrganizationRow = {
  country: {
    label: string | undefined;
  } | null;
  city: string | null | undefined;
  externalId: string | null | undefined;
  id: string;
  jobs: string[];
  name: string;
  ownedEmployerBrands: string[];
  street: string | null | undefined;
  zip: string | null | undefined;
};

export default function OrganizationsListView() {
  const tableRef = useRef<DirectusTableHandle>(undefined);
  const { currentUser } = useLoaderData() as OrganizationsListViewLoaderData;
  const navigate = useNavigate();
  const { isAdminUser, isAgencyUser } = useUserContext();

  const { t } = useTranslation('table');
  const { t: tOrganizationList } = useTranslation('view', {
    keyPrefix: 'organizationList',
  });

  const [open, setOpen] = useState(false);

  const query = useMemo(
    () =>
      ({
        ...organizationTableQuery,
      }) as const satisfies Query<CustomDirectusTypes, Organization>,
    [],
  );

  const actionButtons = useMemo(
    () => (
      <>
        <Button
          data-testid="add-organization-btn"
          label={tOrganizationList('addOrganization')}
          buttonType={ButtonType.SECONDARY_BLUE}
          IconLeading={IconType.Add}
          className="h-12"
          onClick={() => setOpen(true)}
        />
        <Link to={getNewJobWizardUrl()} className="inline-flex">
          <Button
            label={tOrganizationList('addJob')}
            buttonType={ButtonType.PRIMARY}
            IconLeading={IconType.Add}
            className="h-12 w-full"
          />
        </Link>
      </>
    ),
    [tOrganizationList],
  );

  const renderOrgNameCell = useCallback((rowData: OrganizationRow) => {
    const address = `${rowData.street ? `${rowData.street}, ` : ''}${rowData.zip ?? ''} ${rowData.city ? `${rowData.city}, ` : ''}${
      rowData.country?.label ?? ''
    }`;
    return (
      <div className="flex flex-col -ml-3 sm:ml-0">
        <Body4 className="!font-medium sm:!font-normal">{rowData.name}</Body4>
        <Body5 className="text-slate-900 sm:!hidden text-ellipsis">
          {address}
        </Body5>
      </div>
    );
  }, []);

  const selectedColumns = useMemo(() => {
    const columns = ['externalId', 'name', 'employerBrands', 'jobs'];
    if (isAdminUser || isAgencyUser) {
      columns.push('actions');
    }

    return columns;
  }, [isAdminUser, isAgencyUser]);

  const reload = useCallback(() => {
    tableRef.current?.reload(true);
  }, []);

  return (
    <DefaultView
      header={tOrganizationList('title')}
      actionButtons={actionButtons}
    >
      <DirectusTable<
        CustomDirectusTypes,
        'organization',
        typeof query,
        OrganizationsListViewLoaderData
      >
        ref={tableRef}
        itemsPerPage={NO_PAGINATION_PAGE_SIZE}
        collection={'organization'}
        directusClient={directusClient._client}
        query={query}
        additionalProps={{ currentUser }}
        embedded
        selectedColumns={selectedColumns}
        onRowClick={({ id }, event) => {
          if (event?.defaultPrevented) {
            return;
          }
          navigate(`${Paths.ORGANIZATIONS}/${id}`);
        }}
        getRowLink={(id) => `${Paths.ORGANIZATIONS}/${id}`}
        getId={(rowData) => rowData.id}
        columnOverrides={{
          externalId: {
            headContent: t('organization.externalId'),
            renderCell: ({ rowData }) => {
              return rowData.externalId ?? '-';
            },
            sortable: true,
            cellClassName: '!hidden sm:!table-cell',
          },
          name: {
            headContent: t('organization.name'),
            sortable: true,
            renderCell: ({ rowData }) => renderOrgNameCell(rowData),
          },
          employerBrands: {
            headContent: t('organization.employerBrands'),
            renderCell: ({ rowData }) => {
              const combinedBrands = [...rowData.ownedEmployerBrands];

              const uniqueBrands = Array.from(new Set(combinedBrands));

              return uniqueBrands.length;
            },
            // TODO to make this sortable is not trivial since we need to fetch all employer brands
            sortable: false,
            cellClassName: '!hidden sm:!table-cell',
          },
          jobs: {
            headContent: t('organization.jobs'),
            renderCell: ({ rowData }) => {
              return rowData.jobs.length;
            },
            sortable: true,
            cellClassName: '!hidden sm:!table-cell',
          },
          actions: {
            headContent: '',
            sortable: false,
            cellClassName: '!hidden sm:!table-cell sm:min-w-28',
            renderCell: ({ rowData }) => (
              <RowActions rowData={rowData} reload={reload} />
            ),
          },
        }}
      />
      <AddOrganizationDialog open={open} setOpen={setOpen} />
    </DefaultView>
  );
}
